import { useMemo } from "react";
import { useLocale } from "@hooks/useLocale";
import { InlineRowGrid, type InlineColumn } from "@components/input";
import { useMoney } from "@units/useMoney";
import { fmtNum } from "@lib/format";
import {
  useGetReportCostLinesQuery,
  useCreateCostLineMutation,
  useDeleteCostLineMutation,
} from "@api/pApi";
import type { CostLineOut } from "@api/types";

/** A draft for the entry row (all string-typed; validated on commit). */
interface CostDraft {
  cost_date: string;
  categoria: string;
  servicio: string;
  contratista: string;
  precio: string;
  cantidad: string;
}

/**
 * Day cost ENTRY panel (FB2). Lets a user load the day's cost lines from the
 * Cost tab — the piece that was missing (there was a Cost tab but no way to
 * ENTER costs). An inline add-row list (same mechanic as the time log) with
 * fecha · categoría · servicio · contratista · precio · cantidad · total, plus
 * a running total of the day's entered lines.
 *
 * `total` is derived (precio × cantidad) and shown read-only; it is sent on the
 * create body per the contract so P stores the line's total explicitly.
 *
 * Writes go to the oxbo-p cost-line endpoints (POST/GET/DELETE). Visibility is
 * gated upstream by the `afe:manage` capability (see CostPanel); this component
 * assumes it is only mounted when the user may enter costs.
 */
export function CostEntryPanel({
  reportId,
  reportDate,
  currency,
}: {
  reportId: string;
  reportDate?: string;
  currency?: string | null;
}) {
  const { t, locale } = useLocale();
  const { money } = useMoney();

  const { data: lines = [] } = useGetReportCostLinesQuery(reportId, { skip: !reportId });
  const [createCostLine] = useCreateCostLineMutation();
  const [deleteCostLine] = useDeleteCostLineMutation();

  const ccy = currency ?? "USD";

  const emptyDraft = (): CostDraft => ({
    // Default the cost date to the report date — the common case is "today's
    // costs", and the user can change it (it is the COST date; see the note).
    cost_date: reportDate ?? "",
    categoria: "",
    servicio: "",
    contratista: "",
    precio: "",
    cantidad: "",
  });

  // Line total precio × cantidad (null until both are numbers). Single source
  // of truth for the derived TOTAL cell and the stored `total`.
  const lineTotal = (precio: string, cantidad: string): number | null => {
    const p = precio.trim() === "" ? null : Number(precio);
    const q = cantidad.trim() === "" ? null : Number(cantidad);
    if (p == null || q == null || !Number.isFinite(p) || !Number.isFinite(q)) return null;
    return Math.round(p * q * 100) / 100;
  };

  // Running total of the day's entered lines (display-only sum).
  const dayTotal = useMemo(
    () => lines.reduce((s, l) => s + (l.total ?? 0), 0),
    [lines],
  );

  const num = (s: string): number | null => {
    if (s.trim() === "") return null;
    const n = Number(s);
    return Number.isFinite(n) ? n : null;
  };

  const columns: InlineColumn<CostLineOut, CostDraft>[] = [
    {
      key: "cost_date",
      header: t("costentry.col.date"),
      width: 150,
      render: (l) => l.cost_date || "—",
      input: (d, patch) => (
        <input
          type="date"
          aria-label={t("costentry.col.date")}
          value={d.cost_date}
          onChange={(e) => patch({ cost_date: e.target.value })}
        />
      ),
    },
    {
      key: "categoria",
      header: t("costentry.col.category"),
      render: (l) => l.categoria || "—",
      input: (d, patch) => (
        <input
          type="text"
          aria-label={t("costentry.col.category")}
          value={d.categoria}
          placeholder={t("costentry.col.category")}
          onChange={(e) => patch({ categoria: e.target.value })}
        />
      ),
    },
    {
      key: "servicio",
      header: t("costentry.col.service"),
      render: (l) => l.servicio || "—",
      input: (d, patch) => (
        <input
          type="text"
          aria-label={t("costentry.col.service")}
          value={d.servicio}
          placeholder={t("costentry.col.service")}
          onChange={(e) => patch({ servicio: e.target.value })}
        />
      ),
    },
    {
      key: "contratista",
      header: t("costentry.col.contractor"),
      render: (l) => l.contratista || "—",
      input: (d, patch) => (
        <input
          type="text"
          aria-label={t("costentry.col.contractor")}
          value={d.contratista}
          placeholder={t("costentry.col.contractor")}
          onChange={(e) => patch({ contratista: e.target.value })}
        />
      ),
    },
    {
      key: "precio",
      header: t("costentry.col.price"),
      num: true,
      render: (l) => money(l.precio, l.currency ?? ccy),
      input: (d, patch) => (
        <input
          type="number"
          inputMode="decimal"
          aria-label={t("costentry.col.price")}
          value={d.precio}
          onChange={(e) => patch({ precio: e.target.value })}
        />
      ),
    },
    {
      key: "cantidad",
      header: t("costentry.col.qty"),
      num: true,
      render: (l) => (l.cantidad != null ? fmtNum(l.cantidad, locale, { maximumFractionDigits: 2 }) : "—"),
      input: (d, patch) => (
        <input
          type="number"
          inputMode="decimal"
          aria-label={t("costentry.col.qty")}
          value={d.cantidad}
          onChange={(e) => patch({ cantidad: e.target.value })}
        />
      ),
    },
    {
      // TOTAL is DERIVED (precio × cantidad), read-only — a live preview in the
      // entry row, the stored value on committed rows.
      key: "total",
      header: t("costentry.col.total"),
      num: true,
      render: (l) => money(l.total, l.currency ?? ccy),
      input: (d) => {
        const tot = lineTotal(d.precio, d.cantidad);
        return (
          <span className="mono tab-nums dim" aria-label={t("costentry.col.total")}>
            {tot != null ? money(tot, ccy) : "—"}
          </span>
        );
      },
    },
  ];

  const toRow = (d: CostDraft): CostLineOut | null => {
    // Require a cost date + a category (the minimum to be a meaningful line) and
    // a resolvable total (both precio & cantidad present).
    const tot = lineTotal(d.precio, d.cantidad);
    if (!d.cost_date.trim() || !d.categoria.trim() || tot == null) return null;
    return {
      id: `costline-local-${Date.now()}`,
      report_id: reportId,
      cost_date: d.cost_date,
      categoria: d.categoria || null,
      servicio: d.servicio || null,
      contratista: d.contratista || null,
      precio: num(d.precio),
      cantidad: num(d.cantidad),
      total: tot,
      currency: ccy,
    };
  };

  return (
    <div className="card" style={{ marginTop: 14 }}>
      <div className="panel-head-row">
        <h3>{t("costentry.title")}</h3>
      </div>
      <div className="devengado-note">
        <span>{t("costentry.dateNote")}</span>
      </div>
      <div className="table-scroll">
        <InlineRowGrid<CostLineOut, CostDraft>
          ariaLabel={t("costentry.title")}
          columns={columns}
          rows={lines}
          rowKey={(l) => l.id}
          rowOwner={() => "p"}
          emptyDraft={emptyDraft}
          emptyLabel={t("costentry.empty")}
          addLabel={t("costentry.addRow")}
          toRow={toRow}
          onCommit={(row) =>
            createCostLine({
              reportId,
              body: {
                cost_date: row.cost_date,
                categoria: row.categoria,
                servicio: row.servicio,
                contratista: row.contratista,
                precio: row.precio,
                cantidad: row.cantidad,
                total: row.total,
                currency: row.currency ?? ccy,
              },
            })
          }
          onDelete={(row) => {
            if (row.id.startsWith("costline-local-")) return;
            void deleteCostLine({ id: row.id, reportId });
          }}
        />
      </div>

      {/* Running total — sum of the day's entered cost lines (display-only). */}
      <div className="mud-total-row" data-owner="p">
        <span>{t("costentry.dayTotal")}</span>
        <span className="mono tab-nums">{money(dayTotal, ccy)}</span>
      </div>
    </div>
  );
}
